import { useEffect, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import FeatureGates from "@atlassian/feature-gate-js-client";

import { useSearchParameters } from ".";

const UserSegmentationProducts = [
    "confluence.ondemand",
    "jira-software.ondemand",
    "jira-servicedesk.ondemand",
    "jira-core.ondemand"
];

const isProvisioningUserSegmentationProducts = (
    products: readonly string[]
): boolean => {
    const otherProducts = products.filter(
        (product) => !UserSegmentationProducts.includes(product)
    );
    return otherProducts.length === 0;
};

export const useUserSegmentationStatus = (products: readonly string[]) => {
    const { hideUserSegmentation, n2n } = useSearchParameters();
    const jiraSoftware =
        products.includes("jira-software.ondemand") && products.length === 1;

    // Hides user segmentation screens if user is in experiment EXPCEN-4467
    const isUserInNoUserSegAndtemplateCopyUpdatesVariant =
        n2n && jiraSoftware
            ? FeatureGates.getExperimentValue(
                  "jsw_signup_remove_user_segmentation_screens",
                  "noUserSegAndtemplateCopyUpdates",
                  false
              )
            : false;

    const isUserSegHiddenByQueryParam = hideUserSegmentation === "true";
    const [userSegmentationPageIsFinished, setUserSegmentationPageIsFinished] =
        useState(false);
    const [
        alreadyViewedUserSegmentationScreen,
        setAlreadyViewedUserSegmentationScreen
    ] = useState(false);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const listenForUserSegmentationPage = (event: MessageEvent) => {
        let data;
        try {
            data = JSON.parse(event.data);
            if (data.source !== "user-segmentation-ui") {
                data = null;
            }
        } catch (e) {
            data = null;
        }
        if (!data) {
            return;
        }
        if (!data.message) {
            return;
        }
        if (data.message === "finished") {
            if (data.error !== "shouldView was false") {
                createAnalyticsEvent({
                    eventName: "userSegmentationPageClosed",
                    eventType: "UI",
                    eventComponent: "userSegmentationPage",
                    event: "closed",
                    products
                }).fire("signup");
            } else {
                /* The iFramed user segmentation screen makes an API call to check if the user has viewed
        the screen before. If they have, the screen will NOT show again. */
                setAlreadyViewedUserSegmentationScreen(true);
            }
            setUserSegmentationPageIsFinished(true);
        }

        if (data.message === "rendered") {
            createAnalyticsEvent({
                eventName: "userSegmentationPageViewed",
                eventComponent: "userSegmentationPage",
                event: "viewed"
            }).fire("signup");
        }
    };

    useEffect(() => {
        window.addEventListener(
            "message",
            listenForUserSegmentationPage,
            false
        );

        return () => {
            window.removeEventListener(
                "message",
                listenForUserSegmentationPage,
                false
            );
        };
    }, []);

    return {
        /* isEligibleToSeeUserSegmentationPage is agnostic to whether the user has already seen the user segmentation screen.
    since the iFramed screen itself handles this. */
        shouldShowUserSegmentationPage:
            isProvisioningUserSegmentationProducts(products) &&
            !userSegmentationPageIsFinished &&
            !isUserSegHiddenByQueryParam &&
            !isUserInNoUserSegAndtemplateCopyUpdatesVariant,
        isProvisioningUserSegProducts:
            isProvisioningUserSegmentationProducts(products),
        userSegmentationPageIsFinished,
        isUserSegHiddenByQueryParam,
        alreadyViewedUserSegmentationScreen
    };
};
